// predictive-search.js
// Wires the predictive search overlay.
// When window.chavenaSF (Storefront API token) is present, uses the GraphQL
// predictiveSearch query which can return chavena.* metafields (card_gradient,
// tasting_notes). Falls back to /search/suggest.json when the token is absent.

(function () {
  const overlay = document.getElementById('PredictiveSearch');
  const input   = document.getElementById('SearchInput');
  const results = document.getElementById('SearchLive');
  const empty   = document.getElementById('SearchEmpty');

  if (!overlay || !input) return;

  // ── Recent searches (localStorage) ────────────────────────────────────────
  const RECENT_KEY = 'chavena_recent_searches';
  const MAX_RECENT = 4;
  const clockSvg = '<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" fill="none" stroke-width="1.5" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>';

  function getRecent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); }
    catch (e) { return []; }
  }

  function saveRecent(term) {
    if (!term || term.length < 2) return;
    let list = getRecent().filter(t => t !== term);
    list.unshift(term);
    if (list.length > MAX_RECENT) list = list.slice(0, MAX_RECENT);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch (e) {}
  }

  function recentHtml() {
    const recent = getRecent();
    if (!recent.length) return '';
    let html = '<div class="search-col__head">Recent searches</div><div class="search-recent">';
    recent.forEach(term => {
      html += `<button class="search-recent__item" type="button" data-recent-term="${term.replace(/"/g, '&quot;')}">${clockSvg}${term}</button>`;
    });
    return html + '</div>';
  }

  // ── Open / close ───────────────────────────────────────────────────────────
  function open() {
    overlay.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    input.focus();
  }

  function close() {
    overlay.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    input.value = '';
    showEmpty();
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-search-toggle]')) {
      e.preventDefault();
      overlay.classList.contains('is-open') ? close() : open();
    }
    const recentBtn = e.target.closest('[data-recent-term]');
    if (recentBtn && overlay.classList.contains('is-open')) {
      input.value = recentBtn.dataset.recentTerm;
      fetchSuggestions(input.value);
    }
    if (e.target.closest('.search-prod') && overlay.classList.contains('is-open')) {
      saveRecent(input.value.trim());
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    if (e.key === 'Enter' && overlay.classList.contains('is-open') && input.value.trim().length > 1) {
      saveRecent(input.value.trim());
    }
  });

  // ── State helpers ──────────────────────────────────────────────────────────
  function showEmpty() {
    empty.style.display = '';
    results.style.display = 'none';
    results.innerHTML = '';
  }

  function showResults() {
    empty.style.display = 'none';
    results.style.display = 'grid';
  }

  // ── Debounced input ────────────────────────────────────────────────────────
  let debounceTimer;
  input.addEventListener('input', function () {
    clearTimeout(debounceTimer);
    const query = input.value.trim();
    if (query.length < 2) { showEmpty(); return; }
    debounceTimer = setTimeout(() => fetchSuggestions(query), 300);
  });

  function fetchSuggestions(query) {
    window.chavenaSF ? fetchViaSF(query) : fetchViaREST(query);
  }

  // ── Storefront API path ────────────────────────────────────────────────────
  const SF_QUERY = `
    query predictiveSearch($query: String!) {
      predictiveSearch(
        query: $query
        types: [PRODUCT, ARTICLE]
        limitScope: EACH
        limit: 5
        unavailableProducts: LAST
      ) {
        products {
          title
          productType
          onlineStoreUrl
          featuredImage { url(transform: { maxWidth: 400 }) }
          priceRange { minVariantPrice { amount currencyCode } }
          cardGradient: metafield(namespace: "chavena", key: "card_gradient") { value }
          tastingNotes: metafield(namespace: "chavena", key: "tasting_notes") { value }
        }
        articles {
          title
          onlineStoreUrl
          blog { title }
        }
      }
    }
  `;

  function fetchViaSF(query) {
    const token = window.chavenaSF;
    const shop  = window.Shopify && window.Shopify.shop;
    if (!token || !shop) { fetchViaREST(query); return; }

    fetch('https://' + shop + '/api/2024-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query: SF_QUERY, variables: { query: query } })
    })
    .then(r => r.json())
    .then(data => {
      const ps = data.data && data.data.predictiveSearch;
      if (!ps) { showEmpty(); return; }

      const products = (ps.products || []).slice(0, 4).map(function (p) {
        return {
          url:           p.onlineStoreUrl,
          image:         p.featuredImage ? p.featuredImage.url : null,
          product_type:  p.productType,
          title:         p.title,
          price:         formatSFPrice(p.priceRange),
          card_gradient: p.cardGradient ? p.cardGradient.value : null,
          tasting_notes: parseTastingNotes(p.tastingNotes ? p.tastingNotes.value : null)
        };
      });

      const articles = (ps.articles || []).slice(0, 2).map(function (a) {
        return {
          url:        a.onlineStoreUrl,
          title:      a.title,
          blog_title: a.blog ? a.blog.title : null
        };
      });

      render(query, { product: products, article: articles });
    })
    .catch(() => showEmpty());
  }

  function formatSFPrice(priceRange) {
    if (!priceRange || !priceRange.minVariantPrice) return '';
    const v = priceRange.minVariantPrice;
    return parseFloat(v.amount).toLocaleString('en-US', { style: 'currency', currency: v.currencyCode });
  }

  function parseTastingNotes(raw) {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) { return null; }
  }

  // ── REST fallback ──────────────────────────────────────────────────────────
  function fetchViaREST(query) {
    const url = '/search/suggest.json?q=' + encodeURIComponent(query)
              + '&resources[type]=product,article'
              + '&resources[limit]=5'
              + '&resources[options][unavailable_products]=last'
              + '&resources[options][fields]=title,product_type,variants.title,tag,blog_title';

    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(data => {
        const res = data.resources.results;
        const products = (res.product || []).slice(0, 4).map(function (p) {
          return {
            url:           p.url,
            image:         p.image,
            product_type:  p.product_type,
            title:         p.title,
            price:         p.price,
            card_gradient: null,
            tasting_notes: null
          };
        });
        const articles = (res.article || []).slice(0, 2).map(function (a) {
          return {
            url:        a.url,
            title:      a.title,
            blog_title: a.blog_title
          };
        });
        render(query, { product: products, article: articles });
      })
      .catch(() => showEmpty());
  }

  // ── Highlight ──────────────────────────────────────────────────────────────
  function highlight(text, query) {
    if (!text) return '';
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return text.replace(new RegExp(`(${escaped})`, 'gi'), '<span class="hl">$1</span>');
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const DEFAULT_GRADIENT = 'linear-gradient(160deg,#16224A 0%,#0B1432 100%)';

  function render(query, res) {
    const products = res.product || [];
    const articles = res.article || [];
    const i18n     = window.searchI18n || {};

    if (!products.length && !articles.length) {
      results.innerHTML =
        '<div class="search-empty" style="grid-column:1/-1">' +
        '<p style="color:rgba(255,255,255,.45);font-family:var(--serif);font-style:italic;font-size:18px;">' +
        (i18n.noResults || 'No results for') + ` "${query}"</p></div>`;
      showResults();
      return;
    }

    let html = '';

    // Left column — products
    html += '<div class="search-col">';
    html += `<div class="search-col__head">${products.length} ${i18n.products || 'Products'} <span style="color:var(--gold)">— for "${query}"</span></div>`;

    products.forEach(p => {
      const gradient = p.card_gradient || DEFAULT_GRADIENT;
      const notes    = p.tasting_notes ? p.tasting_notes.map(n => n.name).join(' · ') : '';
      html += `<a class="search-prod" href="${p.url}">` +
              `<div class="search-prod__thumb" style="background:${gradient}">` +
              (p.image ? `<img src="${p.image}" alt="" loading="lazy">` : '') +
              `</div>` +
              `<div>` +
              `<div class="search-prod__origin">${p.product_type || ''}</div>` +
              `<div class="search-prod__name">${highlight(p.title, query)}</div>` +
              (notes ? `<div class="search-prod__notes">${notes}</div>` : '') +
              `</div>` +
              `<div class="search-prod__price">${p.price || ''}</div>` +
              `</a>`;
    });

    html += '</div>';

    // Right column — recent searches + articles + browse link
    const hasRecent   = getRecent().length > 0;
    const hasArticles = articles.length > 0;

    html += '<div class="search-col search-col--right">';

    html += recentHtml();

    if (hasArticles) {
      html += `<div class="search-col__head${hasRecent ? '" style="margin-top:20px' : ''}">${i18n.journal || 'Journal'}</div>`;
      articles.forEach(a => {
        const category = a.blog_title || i18n.journal || 'Journal';
        html += `<a class="search-popular__item" href="${a.url}" style="flex-direction:column;align-items:flex-start;gap:4px;">` +
                `<span style="font-size:10px;color:var(--gold);letter-spacing:.18em;text-transform:uppercase;">${category}</span>` +
                `<span class="search-popular__item-name" style="font-size:14px;line-height:1.3;">${highlight(a.title, query)}</span>` +
                `</a>`;
      });
    }

    const topMargin = hasRecent || hasArticles ? '20px' : '0';
    html += `<div class="search-col__head" style="margin-top:${topMargin};">${i18n.allTeas || 'All teas'}</div>`;
    html += `<a class="search-popular__item" href="/collections/all-teas">` +
            `<span class="search-popular__item-name">→ ${i18n.browseAll || 'Browse all 7 destinations'}</span></a>`;

    html += '</div>';

    results.innerHTML = html;
    showResults();
  }
})();
